import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { UIGameActionBoxBase } from '../../UIGameActionBoxBase.tsx';
import { CardTypeEnum } from '@colonist/game-states';
import { UIGameCardInventory } from '../../../UIGameCardInventory/UIGameCardInventory.tsx';
import styles from './UIGameActionBoxPickCard.module.scss';
import { Avatar } from '../../../Avatar/Avatar.tsx';
import { CARD_TIP_TITLE, CARD_POPUP_BODY, cardEnumsToCardState } from '../../../../utils/cards.ts';
import { T } from '@colonist/ui';
import { CardDataController } from '@colonist/game-card-types';
import { ArrayUtils } from '@colonist/utils';
import clsx from 'clsx';
import { useGameStore } from '../../../../store/GameStoreContext.ts';
import { getHandCardsToDisable, getOutOfStockCardsToDisable } from './utils.ts';
function getRemainingCardsToSelect(initialCardsToSelect, selectedCards, removeCardFromSelectionOnClick) {
    if (!removeCardFromSelectionOnClick) return initialCardsToSelect;
    const remainingCardsToSelect = [];
    const selectedCardCounts = cardEnumsToCardState(selectedCards);
    for (const card of initialCardsToSelect){
        var _selectedCardCounts_card;
        const selectedCount = (_selectedCardCounts_card = selectedCardCounts[card]) !== null && _selectedCardCounts_card !== void 0 ? _selectedCardCounts_card : 0;
        if (selectedCount > 0) {
            selectedCardCounts[card] = selectedCount - 1;
        } else {
            remainingCardsToSelect.push(card);
        }
    }
    return remainingCardsToSelect;
}
function getIsEnoughSelected(selectedCardsLength, amountOfCardsToSelect, isSelectFromHand, remainingCardsToSelectLength) {
    const selectedEnough = selectedCardsLength === amountOfCardsToSelect;
    const selectingFromActionBox = !isSelectFromHand;
    const noCardsToSelectLeft = remainingCardsToSelectLength === 0;
    const selectedAtLeastOneCard = selectedCardsLength > 0;
    const actionBoxSelectionIsEmpty = selectingFromActionBox && noCardsToSelectLeft && selectedAtLeastOneCard;
    return selectedEnough || actionBoxSelectionIsEmpty;
}
function getShouldShowSelectedCardFooterInformation(showSelectedCardFooterInformation, amountOfCardsToSelect, selectedCards) {
    return showSelectedCardFooterInformation && amountOfCardsToSelect === 1 && selectedCards.length === 1;
}
function isProgressCard(card) {
    const cardType = CardDataController.getCardTypeFromCardEnum(card);
    return cardType === CardTypeEnum.ProgressTrade || cardType === CardTypeEnum.ProgressPolitics || cardType === CardTypeEnum.ProgressScience;
}
export const UIGameActionBoxPickCard = ({ amountOfCardsToSelect, initialCardsToSelect = [], playerCardState, isSelectFromHand = false, colorTitleOnSelect = false, hideBadgeOfSelectedCards = false, hideBadgeOfCardsToSelect = false, removeCardFromSelectionOnClick = false, showSelectedCardFooterInformation = false, bankStockByCard, onCardSelect, createTextOnSelect, playerValidator, isConfirmDisabled, isPortrait, ...baseProps })=>{
    const [selectedCards, setSelectedCards] = useState([]);
    const setOnSelectCardForActionBox = useGameStore((state)=>state.gameCardInventory.setOnSelectCardForActionBox);
    const setSelectedCardsInInventory = useGameStore((state)=>state.gameCardInventory.setSelectedCards);
    const remainingCardsToSelect = useMemo(()=>getRemainingCardsToSelect(initialCardsToSelect, selectedCards, removeCardFromSelectionOnClick), [
        initialCardsToSelect,
        selectedCards,
        removeCardFromSelectionOnClick
    ]);
    const canAddMoreCards = selectedCards.length < amountOfCardsToSelect;
    const isEnoughSelected = getIsEnoughSelected(selectedCards.length, amountOfCardsToSelect, isSelectFromHand, remainingCardsToSelect.length);
    const shouldDisableConfirm = isConfirmDisabled ? isConfirmDisabled : !isEnoughSelected;
    const handCardsToDisable = useMemo(()=>isSelectFromHand ? getHandCardsToDisable(remainingCardsToSelect, playerCardState) : undefined, [
        isSelectFromHand,
        remainingCardsToSelect,
        playerCardState
    ]);
    const outOfStockCardsToDisable = useMemo(()=>{
        if (isSelectFromHand) return undefined;
        if (bankStockByCard == undefined) return undefined;
        return getOutOfStockCardsToDisable(bankStockByCard, selectedCards, remainingCardsToSelect);
    }, [
        isSelectFromHand,
        bankStockByCard,
        selectedCards,
        remainingCardsToSelect
    ]);
    const setHandCardsToDisable = useGameStore((state)=>state.gameCardInventory.setHandCardsToDisable);
    useEffect(()=>{
        setHandCardsToDisable(handCardsToDisable);
    }, [
        handCardsToDisable
    ]);
    const progressCardsToScaleOnHover = useMemo(()=>{
        const actionBoxCards = [
            ...remainingCardsToSelect,
            ...selectedCards
        ];
        return actionBoxCards.filter(isProgressCard);
    }, [
        remainingCardsToSelect,
        selectedCards
    ]);
    const handleCardClick = useCallback((card, source)=>{
        const clickingSelectableInventory = source === 'hand' || source === 'cardsToSelect';
        const disabledCardsForSource = source === 'hand' ? handCardsToDisable : outOfStockCardsToDisable;
        const isCardDisabled = disabledCardsForSource != undefined && disabledCardsForSource[card] != undefined;
        if (clickingSelectableInventory && isCardDisabled) return;
        const shouldNotUpdateSelection = source === 'hand' && !isSelectFromHand;
        if (shouldNotUpdateSelection) return;
        const shouldRemoveCardFromSelection = source === 'selectedCards';
        if (shouldRemoveCardFromSelection) {
            setSelectedCards((prev)=>{
                const newSelection = ArrayUtils.removeOneElement(prev, card);
                if (newSelection !== prev) onCardSelect(newSelection);
                if (isSelectFromHand) {
                    setTimeout(()=>setSelectedCardsInInventory([
                            ...newSelection
                        ]), 0);
                }
                return newSelection;
            });
            return;
        }
        const shouldReplaceCard = amountOfCardsToSelect == 1;
        const shouldAddOrReplaceCard = canAddMoreCards || shouldReplaceCard;
        if (shouldAddOrReplaceCard) {
            setSelectedCards((prev)=>{
                const newSelection = shouldReplaceCard ? [
                    card
                ] : [
                    ...prev,
                    card
                ];
                onCardSelect(newSelection);
                if (isSelectFromHand) {
                    setTimeout(()=>setSelectedCardsInInventory([
                            ...newSelection
                        ]), 0);
                }
                return newSelection;
            });
        }
    }, [
        canAddMoreCards,
        amountOfCardsToSelect,
        isSelectFromHand,
        onCardSelect,
        handCardsToDisable,
        outOfStockCardsToDisable
    ]);
    useEffect(()=>()=>{
            setSelectedCardsInInventory([]);
        }, []);
    useEffect(()=>{
        setOnSelectCardForActionBox((card)=>handleCardClick(card, 'hand'));
    }, [
        handleCardClick
    ]);
    const currentText = useMemo(()=>{
        if (createTextOnSelect) return createTextOnSelect(baseProps.title, selectedCards.length, amountOfCardsToSelect);
        return {
            title: baseProps.title,
            body: baseProps.body
        };
    }, [
        createTextOnSelect,
        selectedCards.length,
        amountOfCardsToSelect,
        baseProps.title,
        baseProps.body
    ]);
    const handleConfirm = useCallback(()=>{
        if (isEnoughSelected) {
            baseProps.onConfirm();
            setSelectedCardsInInventory([]);
        }
    }, [
        isEnoughSelected,
        baseProps
    ]);
    const validCardsToSelectState = useMemo(()=>cardEnumsToCardState(remainingCardsToSelect), [
        remainingCardsToSelect
    ]);
    const selectedCardsState = useMemo(()=>cardEnumsToCardState(selectedCards), [
        selectedCards
    ]);
    const cardSelectionContent = /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.cardSelectionContainer, {
            [styles.hasScalableCards]: progressCardsToScaleOnHover.length > 0
        })
    }, /*#__PURE__*/ React.createElement(UIGameCardInventory, {
        cardState: isSelectFromHand ? selectedCardsState : validCardsToSelectState,
        onClickCard: (card)=>handleCardClick(card, isSelectFromHand ? 'selectedCards' : 'cardsToSelect'),
        cardsToDisable: outOfStockCardsToDisable,
        forceShowCardBadges: !hideBadgeOfCardsToSelect,
        showTipTitle: false,
        showTipBody: false,
        className: styles.cardSelection,
        cardsToScaleOnHover: progressCardsToScaleOnHover
    }), playerValidator && /*#__PURE__*/ React.createElement("div", {
        className: styles.playerContainer
    }, /*#__PURE__*/ React.createElement(Avatar, {
        className: styles.playerAvatar,
        color: playerValidator.color,
        image: playerValidator.displayIcon,
        profilePictureUrl: playerValidator.profilePictureUrl
    }), /*#__PURE__*/ React.createElement("div", {
        className: styles.playerName
    }, playerValidator.username)));
    const shouldShowSelectedCardFooterInformation = getShouldShowSelectedCardFooterInformation(showSelectedCardFooterInformation, amountOfCardsToSelect, selectedCards);
    const footerContent = !isSelectFromHand ? /*#__PURE__*/ React.createElement("div", {
        className: styles.selectedCardsFooter
    }, /*#__PURE__*/ React.createElement(UIGameCardInventory, {
        cardState: selectedCardsState,
        onClickCard: (card)=>handleCardClick(card, 'selectedCards'),
        showTipTitle: false,
        showTipBody: false,
        className: styles.selectedCardsInventory,
        forceShowCardBadges: !hideBadgeOfSelectedCards,
        cardsToScaleOnHover: progressCardsToScaleOnHover
    }), shouldShowSelectedCardFooterInformation && /*#__PURE__*/ React.createElement("div", {
        className: styles.footerCardInfo
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.cardTitle
    }, /*#__PURE__*/ React.createElement(T, {
        id: CARD_TIP_TITLE[selectedCards[0]]
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.cardBody
    }, /*#__PURE__*/ React.createElement(T, {
        id: CARD_POPUP_BODY[selectedCards[0]]
    })))) : /*#__PURE__*/ React.createElement("div", {
        className: styles.emptyFooter
    });
    const titleColor = colorTitleOnSelect ? isEnoughSelected ? styles.greenTitle : styles.redTitle : undefined;
    return /*#__PURE__*/ React.createElement("div", {
        className: styles.actionBoxWrapper
    }, /*#__PURE__*/ React.createElement(UIGameActionBoxBase, {
        ...baseProps,
        ...isPortrait !== undefined && {
            isPortrait: isPortrait
        },
        title: currentText.title,
        body: currentText.body,
        ...titleColor !== undefined && {
            titleClassName: titleColor
        },
        footerContent: footerContent,
        isConfirmDisabled: shouldDisableConfirm,
        onConfirm: handleConfirm,
        className: styles.hasCardInventory
    }, cardSelectionContent));
};
